import { useApolloClient, useMutation } from '@apollo/react-hooks'
import { useUser } from '@paintscout/ui-business'
import LogRocket from 'logrocket'
import { gql } from 'apollo-boost'
import { useEffect } from 'react'
import userflow from 'userflow.js'

// --- Module-level helpers ---

function setHsOpenCookie(value: boolean) {
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  const paths = ['/', window.location.pathname]
  const segments = window.location.pathname.split('/').filter(Boolean)
  for (let i = 1; i <= segments.length; i++) {
    paths.push('/' + segments.slice(0, i).join('/'))
  }
  // Delete on all paths to avoid duplicates, then set a single clean cookie
  ;[...new Set(paths)].forEach((p) => {
    document.cookie = `hs-messages-is-open=;${expire};path=${p}`
  })
  document.cookie = `hs-messages-is-open=;${expire}`
  document.cookie = `hs-messages-is-open=${value}`
}

function setHubspotVisible(visible: boolean) {
  const el = document.getElementById('hubspot-messages-iframe-container')
  if (!el) return
  if (visible) {
    el.style.removeProperty('display')
  } else {
    el.style.setProperty('display', 'none', 'important')
  }
}

const HS_LOADER_ID = 'hubspot-chat-loader'

function showChatLoader() {
  if (document.getElementById(HS_LOADER_ID)) return
  const loader = document.createElement('div')
  loader.id = HS_LOADER_ID
  Object.assign(loader.style, {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#0766ee',
    zIndex: '2147483646',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 6px rgba(0,0,0,0.2)'
  })
  loader.innerHTML =
    '<div style="width:24px;height:24px;border:3px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:hs-spin 0.8s linear infinite"></div>' +
    '<style>@keyframes hs-spin{to{transform:rotate(360deg)}}</style>'
  document.body.appendChild(loader)
}

function hideChatLoader() {
  document.getElementById(HS_LOADER_ID)?.remove()
}

function setUserflowVisible(visible: boolean) {
  try {
    userflow.setResourceCenterLauncherHidden(!visible)
    if (visible) {
      userflow.openResourceCenter()
    } else {
      userflow.closeResourceCenter()
    }
  } catch {
    // Fallback to CSS if userflow not initialized
    const el = document.getElementById('userflow-ui')
    if (el) el.style.display = visible ? '' : 'none'
  }
}

// --- Module-level state (outside React lifecycle so listeners persist) ---

const state = {
  scriptInjected: false,
  hubspotReady: false,
  eventsRegistered: false,
  closeHandler: null as ((e: MessageEvent) => void) | null,
  logRocketUrl: null as string | null,
  getUser: null as (() => any) | null,
  associateSupportTicket: null as ((opts: any) => void) | null,
  apolloClient: null as any,
  cachedVisitorToken: null as string | null
}

// Listen for HubSpot's postMessage to detect when the chat panel closes.
// Registered dynamically after widget.open() and removed after detecting close.
function startCloseDetection() {
  if (state.closeHandler) window.removeEventListener('message', state.closeHandler)

  let sawOpen = false
  const handler = (event: MessageEvent) => {
    if (typeof event.data !== 'string') return
    try {
      const msg = JSON.parse(event.data)
      if (msg.type !== 'open-change') return
      if (msg.data?.isOpen === true) {
        sawOpen = true
      } else if (msg.data?.isOpen === false && sawOpen) {
        window.removeEventListener('message', handler)
        state.closeHandler = null
        setHsOpenCookie(false)
        setHubspotVisible(false)
        setUserflowVisible(true)
      }
    } catch {
      // not JSON, ignore
    }
  }

  state.closeHandler = handler
  window.addEventListener('message', handler)
}

// --- Open request handler (registered once at module level) ---

const HUBSPOT_VISITOR_TOKEN_QUERY = gql`
  query hubspotVisitorToken {
    hubspotVisitorToken
  }
`

async function fetchVisitorToken() {
  if (state.cachedVisitorToken || !state.apolloClient) return
  try {
    const { data } = await state.apolloClient.query({
      query: HUBSPOT_VISITOR_TOKEN_QUERY,
      fetchPolicy: 'network-only'
    })
    state.cachedVisitorToken = data?.hubspotVisitorToken || null
  } catch {
    // Chat can still open without identification
  }
}

window.addEventListener('ps:open-hubspot-chat', async () => {
  setUserflowVisible(false)

  // If HubSpot widget is already loaded, just show and open it
  if (state.hubspotReady) {
    const hs = (window as any).HubSpotConversations
    setHubspotVisible(true)
    hs.widget.open()
    startCloseDetection()
    return
  }

  // Show loading placeholder while HubSpot loads
  showChatLoader()

  // Fetch visitor token, then inject HubSpot script
  await fetchVisitorToken()

  // Set identification before loading the widget
  const user = state.getUser?.()
  if (user) {
    const settings: Record<string, string> = {
      identificationEmail: user.email
    }
    if (state.cachedVisitorToken) {
      settings.identificationToken = state.cachedVisitorToken
    }
    ;(window as any).hsConversationsSettings = {
      loadImmediately: false,
      ...settings
    }
  } else {
    ;(window as any).hsConversationsSettings = { loadImmediately: false }
  }

  // Inject HubSpot script if not already done
  if (!state.scriptInjected) {
    state.scriptInjected = true
    const script = document.createElement('script')
    script.src = '//js-na3.hs-scripts.com/341850526.js'
    script.async = true
    document.head.appendChild(script)
  }

  // Poll for HubSpotConversations, then load and open the widget
  const interval = setInterval(() => {
    const hs = (window as any).HubSpotConversations
    if (!hs) return
    clearInterval(interval)

    // Register conversationStarted listener once
    if (!state.eventsRegistered) {
      state.eventsRegistered = true
      hs.on('conversationStarted', ({ conversation }: any) => {
        const conversationId = conversation.conversationId
        state.associateSupportTicket?.({
          variables: { conversationId: conversationId.toString(), logRocketUrl: state.logRocketUrl }
        })
      })
    }

    setHsOpenCookie(true)

    // If widget is already loaded, just open it
    if (hs.widget.status().loaded) {
      hideChatLoader()
      hs.widget.open()
      state.hubspotReady = true
      startCloseDetection()
      return
    }

    // Load and open the widget
    hs.widget.load()
    hs.widget.open()
    state.hubspotReady = true

    // Once iframe appears, keep loader behind HubSpot briefly so there's no flash of empty space
    const loaderCheck = setInterval(() => {
      const iframe = document.getElementById('hubspot-conversations-iframe')
      if (iframe) {
        clearInterval(loaderCheck)
        setTimeout(hideChatLoader, 2000)
      }
    }, 100)

    startCloseDetection()
  }, 100)
})

// --- React component (wires up dynamic values to module state) ---

export default function HubspotChat() {
  const { user } = useUser()
  const apolloClient = useApolloClient()
  const [associateSupportTicket] = useMutation(gql`
    mutation associateSupportTicket($conversationId: String!, $logRocketUrl: String) {
      associateSupportTicket(conversationId: $conversationId, logRocketUrl: $logRocketUrl)
    }
  `)

  state.getUser = () => user
  state.associateSupportTicket = associateSupportTicket
  state.apolloClient = apolloClient

  useEffect(() => {
    try {
      LogRocket.getSessionURL((sessionURL) => {
        state.logRocketUrl = sessionURL
      })
    } catch {
      // LogRocket not available
    }
  }, [])

  return null
}
